/**
 * Classification of the order-creation response.
 *
 * The sign-up provider makes one decision per order attempt: given the HTTP
 * response and its parsed body, which of eleven outcomes happened. That decision
 * is pure -- it reads the wire response and nothing else -- so it lives here as
 * a function the provider calls, and the provider keeps the state writes and
 * the rendered messages each outcome implies.
 *
 * Rules for this module:
 * - No React, no context, no imports from SignUpProvider. Response in, outcome
 *   out, so it is unit-testable without a render.
 * - It returns a kind, never rendered text. Messages that reach the user are
 *   the provider's job, because two of them are localised through intl and the
 *   rest are read straight off the response body.
 * - ORDER IS SEMANTIC: several arms overlap, so reordering them changes which
 *   outcome a response gets. The arms fail closed -- a response that matches no
 *   recognised shape is `unrecognized`, never `completed`.
 *
 * This does NOT live in the transition table. The table routes on the
 * provider's own state (user, plan, validation, jwt, order); this routes on a
 * response that has not been merged into that state yet. Only the `completed`
 * outcome reaches the table, as the outcome the provider feeds it afterwards.
 */

import { ERROR_TYPES } from '@/utils/errors'

/** The user-facing text for outcomes that deliberately hide the upstream error. */
export const ORDER_FAILURE_MESSAGE = 'Sorry, we had a problem processing your order. Please re-check your information and try again.'

/**
 * Upstream messages too generic to show a customer. A response carrying one of
 * these is reported as ORDER_FAILURE_MESSAGE instead of verbatim.
 */
export const errorsToIgnore = ['Bad Request', 'Internal Server Error']

/**
 * The only positive signal that the session really expired: the message the
 * order API route sends from its CSRF branch, in src/pages/api/signup/create.js.
 * Every other 400 is some other failure wearing the same transport status.
 */
export const SESSION_EXPIRED_MESSAGE = 'Session expired'

/**
 * The eleven outcomes of an order attempt.
 *
 * `errorGeneric`/`errorSpecific` and `statusGeneric`/`statusSpecific` are two
 * pairs that produce the same state writes as each other but arise from
 * different branches -- an `error` object versus a bare `status`. They stay
 * four outcomes rather than two so that losing one branch is visible.
 *
 * `serverFailure` and `unrecognized` are the two closed doors: a response the
 * upstream could not serve, and a response whose shape nothing here recognises.
 */
export const ORDER_OUTCOMES = {
  noCcTrialOffered: 'noCcTrialOffered',
  validationFailed: 'validationFailed',
  tooManyCcAttempts: 'tooManyCcAttempts',
  errorGeneric: 'errorGeneric',
  errorSpecific: 'errorSpecific',
  statusGeneric: 'statusGeneric',
  statusSpecific: 'statusSpecific',
  sessionExpired: 'sessionExpired',
  serverFailure: 'serverFailure',
  unrecognized: 'unrecognized',
  completed: 'completed',
}

/**
 * Classify one order-creation response.
 *
 * @param {Response} res        the fetch Response -- only `res.status` is read
 * @param {object} resMessage   the parsed body, which may be any JSON value
 * @returns {string} one of ORDER_OUTCOMES
 */
export const classifyOrderResponse = (res, resMessage) => {
  if (!resMessage || typeof resMessage !== 'object' || Array.isArray(resMessage)) {
    return ORDER_OUTCOMES.unrecognized
  }

  if (resMessage.type === ERROR_TYPES.requestValidationFailed) {
    return resMessage.showNoCCOffer ? ORDER_OUTCOMES.noCcTrialOffered : ORDER_OUTCOMES.validationFailed
  }

  if (resMessage.type === ERROR_TYPES.tooManyCcAttempts) {
    return ORDER_OUTCOMES.tooManyCcAttempts
  }

  if (res.status >= 500 || resMessage.status >= 500) {
    return ORDER_OUTCOMES.serverFailure
  }

  if (resMessage.error) {
    return typeof resMessage.error.message === 'string' && !errorsToIgnore.includes(resMessage.error.message)
      ? ORDER_OUTCOMES.errorSpecific
      : ORDER_OUTCOMES.errorGeneric
  }

  if (res.status === 400 && resMessage.message === SESSION_EXPIRED_MESSAGE) {
    return ORDER_OUTCOMES.sessionExpired
  }

  if (resMessage.status >= 300 && resMessage.status < 500) {
    return typeof resMessage.message === 'string' && !errorsToIgnore.includes(resMessage.message)
      ? ORDER_OUTCOMES.statusSpecific
      : ORDER_OUTCOMES.statusGeneric
  }

  if (res.status >= 400) {
    return ORDER_OUTCOMES.unrecognized
  }

  return resMessage.id ? ORDER_OUTCOMES.completed : ORDER_OUTCOMES.unrecognized
}
